import { httpsCallable } from "../../../../../shared/legacyData/legacyCallable";
import { CLAIM_PROCESSED_STATUSES, CLAIM_STATUS, normalizeClaimWorkflowStatus } from "./claimWorkflow";
import { functions } from "../../../../../firebase";
import { invokeFinancialCallable } from "../../../../../financialDashboard/api/financialCallableTransport";
import { financialDashboardApi } from "../../../../../financialDashboard/api/financialDashboardApi";
import { isFinancialTenantScopedActive } from "../../../../../financialDashboard/auth/FinancialTenantContext";
import {
  buildFinancialClaimTenantPayload,
  resolveFinancialClaimFetchScopes,
} from "./claimCollectionBrand";
export { formatClaimAuditEventLabel } from "../components/claimAuditPresentation";

const claimsCache = new Map();
const claimsInFlight = new Map();
const CACHE_TTL_MS = 3 * 60 * 1000;
const TENANT_CLAIMS_CACHE_KEY = "tenant::financial-claims";
const CLAIM_FN_TIMEOUT_MS = 120000;
const CLAIM_OCR_FN_TIMEOUT_MS = 300000;
const CLAIMS_FETCH_ATTEMPTS = 2;

export const invalidateFinancialClaimsCache = (company, collectionBrand) => {
  if (isFinancialTenantScopedActive()) {
    claimsCache.delete(TENANT_CLAIMS_CACHE_KEY);
    claimsInFlight.delete(TENANT_CLAIMS_CACHE_KEY);
    return;
  }
  if (!company) return;
  // Combined brand+shared fetches are cached under the auth collectionBrand key.
  // Always clear every cache entry for the company so shared-sourced writes cannot
  // leave a stale auth-brand snapshot (e.g. claim still PENDING in Review).
  const prefix = `${company}::`;
  for (const key of [...claimsCache.keys()]) {
    if (key.startsWith(prefix)) claimsCache.delete(key);
  }
  for (const key of [...claimsInFlight.keys()]) {
    if (key.startsWith(prefix)) claimsInFlight.delete(key);
  }
  if (collectionBrand) {
    claimsCache.delete(`${company}::${collectionBrand}`);
    claimsInFlight.delete(`${company}::${collectionBrand}`);
  }
  claimsCache.delete(`${company}::shared`);
  claimsInFlight.delete(`${company}::shared`);
};

export const parseUserEnteredAmount = (value) => {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;

  let text = String(value).trim();
  if (!text) return 0;
  text = text.replace(/ZAR/gi, "").replace(/[Rr](?=[\s\d.,-])|\$/g, "");
  text = text.replace(/[\s\u00a0\u202f]/g, "");

  const lastComma = text.lastIndexOf(",");
  const lastDot = text.lastIndexOf(".");
  if (lastComma !== -1 && lastDot !== -1) {
    text =
      lastComma > lastDot
        ? text.replace(/\./g, "").replace(",", ".")
        : text.replace(/,/g, "");
  } else if (lastComma !== -1) {
    const fraction = text.slice(lastComma + 1);
    text = /^\d{1,2}$/.test(fraction)
      ? `${text.slice(0, lastComma)}.${fraction}`
      : text.replace(/,/g, "");
  }

  text = text.replace(/[^0-9.-]/g, "");
  if (!text || text === "-" || text === ".") return 0;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeNumber = (value) => parseUserEnteredAmount(value);

export const formatCurrency = (value) =>
  new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(normalizeNumber(value));

export const normalizeClaimStatus = (status) => normalizeClaimWorkflowStatus(status);

const normalizeDateString = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toISOString().slice(0, 10);
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const b64 = result.includes(",") ? result.split(",").pop() : result;
      resolve(b64 || "");
    };
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });

export const formatClaimId = (rawId) => {
  const source = String(rawId || "");
  const digits = source.replace(/\D/g, "");
  if (digits.length >= 4) {
    return `CLM-${digits.slice(-4)}`;
  }
  const fallback = source.replace(/[^a-zA-Z0-9]/g, "").slice(-4).toUpperCase();
  return `CLM-${fallback || "0000"}`;
};

const firstNonEmptyString = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate == null) continue;
    if (typeof candidate === "object") {
      const nested = firstNonEmptyString(
        candidate.name,
        candidate.fullName,
        candidate.displayName,
        candidate.email,
      );
      if (nested) return nested;
      continue;
    }
    const s = String(candidate).trim();
    if (s) return s;
  }
  return "";
};

const invokeClaimCallable = (name, input, timeout = CLAIM_FN_TIMEOUT_MS) =>
  invokeFinancialCallable(name, input, { timeout });

const unwrapClaimCallableResult = (response) => {
  const top = response?.data || {};
  const inner = top?.data != null && typeof top.data === "object" ? top.data : top;
  return {
    ok: top.ok !== false,
    claim: inner?.claim || top?.claim || null,
    claims: inner?.claims || null,
    popValidation: inner?.popValidation || null,
    claimsOcrOptions: inner?.claimsOcrOptions || null,
    paymentBatchId: inner?.paymentBatchId || null,
    relatedApprovedClaims: inner?.relatedApprovedClaims || null,
    claimsSummary: inner?.claimsSummary || null,
    paymentBatchTotal: inner?.paymentBatchTotal ?? null,
    supplierCreditSummary:
      inner?.supplierCreditSummary ||
      top?.supplierCreditSummary ||
      (inner?.availableCredit != null ? inner : null),
    raw: top,
  };
};

const buildTenantPayload = ({ company, claim, fallbackCollectionBrand }) =>
  buildFinancialClaimTenantPayload({
    company,
    claim,
    fallbackCollectionBrand,
  });

/** Display label for the user who created / requested the claim (not supplier or invoice name). */
export const getClaimRequesterLabel = (claim) => {
  if (!claim) return "-";
  const displayName = firstNonEmptyString(
    claim.createdByName,
    claim.submittedByName,
    claim.requesterName,
    claim.claimantName,
    claim.requestedByName,
    claim.submitterName,
    claim.userDisplayName,
    claim.requester,
    typeof claim.submittedBy === "object" ? claim.submittedBy : null,
  );
  if (displayName) return displayName;
  const emailOrId = firstNonEmptyString(
    claim.createdBy,
    typeof claim.submittedBy === "string" ? claim.submittedBy : null,
    claim.userEmail,
    claim.email,
    claim.requestedBy,
  );
  return emailOrId || "-";
};

/* Display label for the supplier / payee on a claim */
export const getClaimSupplierLabel = (claim) => {
  if (!claim) return "-";
  const label = firstNonEmptyString(
    claim.supplier,
    claim.supplierName,
    claim.supplierNameOnInvoice,
    claim.payeeName,
    claim.vendorName,
    claim.invoiceSupplierValidation?.selectedSupplier?.supplierDisplayName,
    claim.invoiceSupplierValidation?.extractedSubset?.supplierNameOnInvoice,
  );
  return label || "-";
};

const callGetSystemDocumentsWithRetry = async (payload) => {
  const getSystemDocuments = httpsCallable(functions, "getSystemDocuments");
  let lastError = null;
  for (let attempt = 1; attempt <= CLAIMS_FETCH_ATTEMPTS; attempt += 1) {
    try {
      const response = await getSystemDocuments(payload);
      if (response?.data?.success) return response;
      lastError = new Error(
        `getSystemDocuments returned unsuccessful response for ${payload.collectionBrand}/${payload.systemName}`,
      );
    } catch (error) {
      lastError = error;
    }
    if (attempt < CLAIMS_FETCH_ATTEMPTS) {
      await new Promise((resolve) => setTimeout(resolve, 350 * attempt));
    }
  }
  throw lastError || new Error("Failed to fetch claims documents.");
};

const normalizeFinancialClaimRow = (claim, scopeBrand) => {
  const status = normalizeClaimStatus(claim.status);
  const isProcessed = CLAIM_PROCESSED_STATUSES.has(status);
  return {
    ...claim,
    _sourceCollectionBrand: scopeBrand,
    status,
    processedDate: isProcessed
      ? normalizeDateString(
          claim.processedDate || claim.updatedAt || claim.closedAt || claim.finalDecisionAt,
        )
      : "-",
    numericAmount: normalizeNumber(
      claim.numericAmount != null ? claim.numericAmount : claim.totalAmount,
    ),
    displayId: formatClaimId(claim.id),
    submittedDate:
      status === CLAIM_STATUS.DRAFT
        ? "-"
        : normalizeDateString(
            claim.submittedDate || claim.submittedAt || claim.date || claim.createdAt,
          ),
    notes:
      claim.notes ||
      claim.note ||
      claim.secondReviewNotes ||
      claim.firstReviewNotes ||
      claim.finalDecisionNotes ||
      claim.manualReviewNotes ||
      claim.rejectionReason ||
      claim.reason ||
      claim.comment ||
      "-",
    claimRequesterLabel: getClaimRequesterLabel(claim),
  };
};

export const fetchFinancialClaims = async ({
  company,
  collectionBrand,
  force = false,
} = {}) => {
  if (isFinancialTenantScopedActive()) {
    if (!force) {
      const cached = claimsCache.get(TENANT_CLAIMS_CACHE_KEY);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
        return cached.data;
      }
      const inFlight = claimsInFlight.get(TENANT_CLAIMS_CACHE_KEY);
      if (inFlight) return inFlight;
    } else {
      claimsCache.delete(TENANT_CLAIMS_CACHE_KEY);
      claimsInFlight.delete(TENANT_CLAIMS_CACHE_KEY);
    }

    const requestPromise = (async () => {
      const response = await financialDashboardApi.listClaims({ force });
      const tagged = (Array.isArray(response?.claims) ? response.claims : [])
        .map((claim) =>
          normalizeFinancialClaimRow(
            claim,
            claim?._sourceCollectionBrand || response?.collectionBrand || "shared",
          ),
        );
      claimsCache.set(TENANT_CLAIMS_CACHE_KEY, {
        timestamp: Date.now(),
        data: tagged,
      });
      return tagged;
    })();

    claimsInFlight.set(TENANT_CLAIMS_CACHE_KEY, requestPromise);
    try {
      return await requestPromise;
    } finally {
      if (claimsInFlight.get(TENANT_CLAIMS_CACHE_KEY) === requestPromise) {
        claimsInFlight.delete(TENANT_CLAIMS_CACHE_KEY);
      }
    }
  }

  if (!company || !collectionBrand) return [];

  const cacheKey = `${company}::${collectionBrand}`;
  if (!force) {
    const cached = claimsCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return cached.data;
    }
    const inFlight = claimsInFlight.get(cacheKey);
    if (inFlight) return inFlight;
  } else {
    claimsCache.delete(cacheKey);
    claimsInFlight.delete(cacheKey);
  }

  const requestPromise = (async () => {
    const scopes = resolveFinancialClaimFetchScopes(company, collectionBrand);

    const settled = await Promise.all(
      scopes.map(async (scopeBrand) => {
        try {
          const response = await callGetSystemDocumentsWithRetry({
            company,
            collectionBrand: scopeBrand,
            systemName: "claims",
            auth: true,
          });
          return {
            scopeBrand,
            ok: true,
            rows: Array.isArray(response?.data?.data) ? response.data.data : [],
          };
        } catch (error) {
          return { scopeBrand, ok: false, rows: [], error };
        }
      }),
    );

    const anySuccess = settled.some((entry) => entry.ok);
    if (!anySuccess) {
      const firstError = settled.find((entry) => entry.error)?.error;
      throw firstError || new Error("Could not load claims. Please try again.");
    }

    const seenIds = new Set();
    const tagged = [];
    settled.forEach((entry) => {
      if (!entry.ok) return;
      entry.rows.forEach((claim) => {
        const id = String(claim?.id || "").trim();
        if (id) {
          if (seenIds.has(id)) return;
          seenIds.add(id);
        }
        tagged.push(normalizeFinancialClaimRow(claim, entry.scopeBrand));
      });
    });

    claimsCache.set(cacheKey, {
      timestamp: Date.now(),
      data: tagged,
    });
    return tagged;
  })();

  claimsInFlight.set(cacheKey, requestPromise);
  try {
    return await requestPromise;
  } finally {
    if (claimsInFlight.get(cacheKey) === requestPromise) {
      claimsInFlight.delete(cacheKey);
    }
  }
};

export const submitFinancialClaimReviewDecision = async ({
  company,
  claim,
  decision,
  reviewNotes,
  fallbackCollectionBrand,
}) => {
  if (!claim?.id) throw new Error("Cannot review claim without id.");
  const response = await invokeClaimCallable("submitFinancialClaimReviewDecision", {
    tenant: buildTenantPayload({ company, claim, fallbackCollectionBrand }),
    claimId: claim.id,
    decision,
    reviewNotes,
  });
  const result = unwrapClaimCallableResult(response);
  invalidateFinancialClaimsCache(company, claim._sourceCollectionBrand || fallbackCollectionBrand);
  return result;
};

export const submitFinancialClaimCfoDecision = async ({
  company,
  claim,
  decision,
  decisionNotes,
  fallbackCollectionBrand,
}) => {
  if (!claim?.id) throw new Error("Cannot decide claim without id.");
  const response = await invokeClaimCallable("submitFinancialClaimCfoDecision", {
    tenant: buildTenantPayload({ company, claim, fallbackCollectionBrand }),
    claimId: claim.id,
    decision,
    decisionNotes: decisionNotes ? String(decisionNotes).trim() : "",
  });
  const result = unwrapClaimCallableResult(response);
  invalidateFinancialClaimsCache(company, claim._sourceCollectionBrand || fallbackCollectionBrand);
  return result;
};

export const replaceFinancialClaimAmendmentEvidence = async ({
  company,
  claim,
  evidenceFile,
  fallbackCollectionBrand,
}) => {
  if (!claim?.id) throw new Error("Cannot replace invoice without claim id.");
  if (!evidenceFile) throw new Error("Replacement invoice file is required.");
  const base64 = await fileToBase64(evidenceFile);
  if (!base64) throw new Error("Replacement invoice file is required.");
  const response = await invokeClaimCallable(
    "replaceFinancialClaimAmendmentEvidence",
    {
      tenant: buildTenantPayload({ company, claim, fallbackCollectionBrand }),
      claimId: claim.id,
      fileName: evidenceFile.name,
      fileBuffer: base64,
    },
    CLAIM_OCR_FN_TIMEOUT_MS,
  );
  const result = unwrapClaimCallableResult(response);
  invalidateFinancialClaimsCache(company, claim._sourceCollectionBrand || fallbackCollectionBrand);
  return result;
};

export const resubmitFinancialClaimAmendment = async ({
  company,
  claim,
  invoiceNumber,
  totalAmount,
  purpose,
  duplicateInvoiceAcknowledged,
  fallbackCollectionBrand,
}) => {
  if (!claim?.id) throw new Error("Cannot resubmit claim without id.");
  const response = await invokeClaimCallable("resubmitFinancialClaimAmendment", {
    tenant: buildTenantPayload({ company, claim, fallbackCollectionBrand }),
    claimId: claim.id,
    invoiceNumber,
    totalAmount,
    purpose,
    ...(duplicateInvoiceAcknowledged
      ? { duplicateInvoiceAcknowledged }
      : {}),
  });
  const result = unwrapClaimCallableResult(response);
  invalidateFinancialClaimsCache(company, claim._sourceCollectionBrand || fallbackCollectionBrand);
  return result;
};

const buildPopFilePayload = async (popFile) => {
  if (!popFile) throw new Error("Proof of payment file is required.");
  const base64 = await fileToBase64(popFile);
  if (!base64) throw new Error("Proof of payment file is required.");
  return {
    fileName: popFile.name,
    fileBuffer: base64,
    contentType: popFile.type || "",
  };
};

export const validateFinancialClaimProofOfPayment = async ({
  company,
  claimIds,
  popFile,
  fallbackCollectionBrand,
  anchorClaim = null,
  claimPaymentScopes = null,
}) => {
  const ids = Array.isArray(claimIds)
    ? claimIds.map((entry) => String(entry || "").trim()).filter(Boolean)
    : [];
  if (!ids.length && anchorClaim?.id) {
    ids.push(String(anchorClaim.id));
  }
  if (!ids.length) throw new Error("At least one claim id is required.");
  const popPayload = await buildPopFilePayload(popFile);
  const response = await invokeClaimCallable(
    "validateFinancialClaimProofOfPayment",
    {
      tenant: buildTenantPayload({
        company,
        claim: anchorClaim || { id: ids[0] },
        fallbackCollectionBrand,
      }),
      claimIds: ids,
      claimPaymentScopes: claimPaymentScopes || undefined,
      popFile: popPayload,
    },
    CLAIM_OCR_FN_TIMEOUT_MS,
  );
  return unwrapClaimCallableResult(response);
};

export const submitFinancialClaimPaymentDecision = async ({
  company,
  claim,
  claimIds,
  paymentNotes,
  popFile,
  notifySupplier = false,
  recordSupplierCredit = false,
  supplierCreditNotes = "",
  priorPaymentArrangement = "",
  priorPaymentArrangementNotes = "",
  priorPaymentArrangementDate = "",
  acceptPartialPayment = false,
  popValidationOverride = null,
  claimPaymentScopes = null,
  fallbackCollectionBrand,
}) => {
  const ids = Array.isArray(claimIds)
    ? claimIds.map((entry) => String(entry || "").trim()).filter(Boolean)
    : claim?.id
      ? [String(claim.id)]
      : [];
  if (!ids.length) throw new Error("Cannot mark claims as paid without claim ids.");
  const popPayload = await buildPopFilePayload(popFile);
  const response = await invokeClaimCallable(
    "submitFinancialClaimPaymentDecision",
    {
      tenant: buildTenantPayload({ company, claim: claim || { id: ids[0] }, fallbackCollectionBrand }),
      claimIds: ids,
      claimId: ids[0],
      paymentNotes: paymentNotes ? String(paymentNotes).trim() : "",
      notifySupplier: Boolean(notifySupplier),
      recordSupplierCredit: Boolean(recordSupplierCredit),
      supplierCreditNotes: supplierCreditNotes ? String(supplierCreditNotes).trim() : "",
      acceptPartialPayment: Boolean(acceptPartialPayment),
      priorPaymentArrangement:
        priorPaymentArrangement != null && String(priorPaymentArrangement).trim() !== ""
          ? String(priorPaymentArrangement).trim()
          : undefined,
      priorPaymentArrangementNotes: priorPaymentArrangementNotes
        ? String(priorPaymentArrangementNotes).trim()
        : "",
      priorPaymentArrangementDate: priorPaymentArrangementDate
        ? String(priorPaymentArrangementDate).trim()
        : undefined,
      popValidationOverride: popValidationOverride || undefined,
      claimPaymentScopes: claimPaymentScopes || undefined,
      popFile: popPayload,
    },
    CLAIM_OCR_FN_TIMEOUT_MS,
  );
  const result = unwrapClaimCallableResult(response);
  invalidateFinancialClaimsCache(company, claim?._sourceCollectionBrand || fallbackCollectionBrand);
  return result;
};

/** Close a partly paid claim without paying the remaining balance (reason required for audit). */
export const submitFinancialClaimPartialPaymentCompletion = async ({
  company,
  claim,
  claimIds,
  completionReason,
  fallbackCollectionBrand,
}) => {
  const ids = Array.isArray(claimIds)
    ? claimIds.map((entry) => String(entry || "").trim()).filter(Boolean)
    : claim?.id
      ? [String(claim.id)]
      : [];
  if (!ids.length) {
    throw new Error("Cannot complete claim without claim ids.");
  }
  const reason = String(completionReason || "").trim();
  if (!reason) {
    throw new Error("A completion reason is required for audit purposes.");
  }
  const response = await invokeClaimCallable("submitFinancialClaimPartialPaymentCompletion", {
    tenant: buildTenantPayload({
      company,
      claim: claim || { id: ids[0] },
      fallbackCollectionBrand,
    }),
    claimIds: ids,
    claimId: ids[0],
    completionReason: reason,
  });
  const result = unwrapClaimCallableResult(response);
  invalidateFinancialClaimsCache(
    company,
    claim?._sourceCollectionBrand || fallbackCollectionBrand,
  );
  return result;
};

export const fetchFinancialClaimSupplierCreditSummary = async ({
  company,
  claim,
  supplierKey,
  fallbackCollectionBrand,
}) => {
  const response = await invokeClaimCallable("getFinancialClaimSupplierCreditSummary", {
    tenant: buildTenantPayload({ company, claim, fallbackCollectionBrand }),
    supplierKey: supplierKey ? String(supplierKey).trim() : undefined,
    claimId: claim?.id,
  });
  const result = unwrapClaimCallableResult(response);
  return result.supplierCreditSummary || result.raw?.data || result.raw || null;
};

export const deleteFinancialClaimDraft = async ({
  company,
  claim,
  fallbackCollectionBrand,
}) => {
  if (!claim?.id) throw new Error("Cannot delete draft without claim id.");
  const response = await invokeClaimCallable("deleteFinancialClaimDraft", {
    tenant: buildTenantPayload({ company, claim, fallbackCollectionBrand }),
    claimId: claim.id,
  });
  const result = unwrapClaimCallableResult(response);
  invalidateFinancialClaimsCache(company, claim._sourceCollectionBrand || fallbackCollectionBrand);
  return result;
};

export const submitFinancialClaimNotesEdit = async ({
  company,
  claim,
  notes,
  fallbackCollectionBrand,
}) => {
  if (!claim?.id) throw new Error("Cannot edit notes without claim id.");
  const response = await invokeClaimCallable("submitFinancialClaimNotesEdit", {
    tenant: buildTenantPayload({ company, claim, fallbackCollectionBrand }),
    claimId: claim.id,
    notes: String(notes || "").trim(),
  });
  const result = unwrapClaimCallableResult(response);
  invalidateFinancialClaimsCache(company, claim._sourceCollectionBrand || fallbackCollectionBrand);
  return result;
};

const formatAuditDateTimeParts = (timestamp) => {
  if (!timestamp) return { dateDisplay: "N/A", timeDisplay: "N/A" };
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return { dateDisplay: "N/A", timeDisplay: "N/A" };
  return {
    dateDisplay: date.toISOString().slice(0, 10),
    timeDisplay: date.toISOString().slice(11, 16),
  };
};

const normalizeAuditRow = (row = {}) => {
  const timestamp = row.timestamp || row.createdAt || row.updatedAt || null;
  const { dateDisplay, timeDisplay } = formatAuditDateTimeParts(timestamp);
  return {
    ...row,
    auditDocId: row.auditDocId || row.id || null,
    claimId: row.claimId || row.id || null,
    eventType: row.eventType || row.action || "workflow_event",
    actorEmail: row.actorEmail || row.actor || null,
    actorUid: row.actorUid || row.uid || null,
    summary: row.summary || row.notes || null,
    reviewNotes: row.notes || row.reviewNotes || null,
    ipAddress: row.ipAddress || null,
    timestamp,
    dateDisplay,
    timeDisplay,
  };
};

export const fetchFinancialClaimsAuditLog = async ({
  company,
  collectionBrand,
  claimId,
  eventTypes,
  actor,
  query,
  from,
  to,
  limit = 100,
  cursor = null,
}) => {
  if (!company || !collectionBrand) {
    return {
      rows: [],
      totalEvents: 0,
      today: 0,
      thisWeek: 0,
      activeUsers: 0,
      nextCursor: null,
    };
  }
  const response = await invokeClaimCallable("getFinancialClaimsAuditLog", {
    tenant: buildFinancialClaimTenantPayload({ company, collectionBrand }),
    claimId: claimId || "",
    eventTypes: Array.isArray(eventTypes) ? eventTypes : [],
    actor: actor || "",
    query: query || "",
    from: from || null,
    to: to || null,
    limit,
    cursor: cursor || null,
  });
  const top = response?.data || {};
  const inner = top?.data != null && typeof top.data === "object" ? top.data : top;
  const rows = Array.isArray(inner?.rows) ? inner.rows.map((row) => normalizeAuditRow(row)) : [];
  return {
    rows,
    totalEvents: Number(inner?.totalEvents || rows.length || 0),
    today: Number(inner?.today || 0),
    thisWeek: Number(inner?.thisWeek || 0),
    activeUsers: Number(inner?.activeUsers || 0),
    nextCursor: inner?.nextCursor || null,
  };
};

export const fetchFinancialClaimAuditEntries = async ({
  company,
  claim,
  fallbackCollectionBrand,
}) => {
  const claimId = claim?.id || null;
  if (!company || !claimId) return [];
  const response = await invokeClaimCallable("getFinancialClaimAuditEntries", {
    tenant: buildFinancialClaimTenantPayload({
      company,
      claim,
      fallbackCollectionBrand,
    }),
    claimId,
  });
  const top = response?.data || {};
  const inner = top?.data != null && typeof top.data === "object" ? top.data : top;
  const rows = Array.isArray(inner?.rows) ? inner.rows : [];
  return rows
    .map((row) => normalizeAuditRow(row))
    .sort((a, b) => new Date(a.timestamp || 0).getTime() - new Date(b.timestamp || 0).getTime());
};

/** Mint a fresh Firebase Storage token URL for claim evidence or POP files. */
export const ensureFinancialClaimEvidenceDownloadUrl = async ({
  claimId,
  storagePath,
  company,
  collectionBrand,
  authCollectionBrand,
} = {}) => {
  const response = await invokeClaimCallable("ensureFinancialClaimEvidenceDownloadUrl", {
    claimId,
    storagePath,
    tenant: buildFinancialClaimTenantPayload({
      company,
      collectionBrand,
      fallbackCollectionBrand: authCollectionBrand || collectionBrand,
    }),
  });
  const top = response?.data || {};
  const inner = top?.data != null && typeof top.data === "object" ? top.data : top;
  return {
    storagePath: inner?.storagePath || storagePath || null,
    url: inner?.url || null,
  };
};
